import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchAllRoleplays } from "../../redux/actions/roleplayActions";
import { fetchOrganizations } from "../../redux/actions/organizationActions";

interface RoleplaysTabProps {
  organizationId?: string;
}

const RoleplaysTab: React.FC<RoleplaysTabProps> = ({ organizationId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize search term and org filter from URL parameters
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
  const [filterOrganization, setFilterOrganization] = useState(() => searchParams.get("org") || "");
  const [filterEnvironment, setFilterEnvironment] = useState("");

  const { roleplays, loading, error } = useSelector(
    (state: RootState) => state.roleplays
  );
  const { organizations } = useSelector(
    (state: RootState) => state.organizations
  );

  useEffect(() => {
    const params: any = {};
    if (organizationId) {
      params.organizationId = organizationId;
    }

    dispatch(fetchAllRoleplays(params));

    if (!organizationId) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, organizationId]);

  // Sync search term and org filter from URL when it changes externally
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlOrg = searchParams.get("org") || "";
    setSearchTerm(urlSearch);
    setFilterOrganization(urlOrg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Update URL when filters change
  const updateUrlParams = useCallback((search: string, org: string) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (org) params.set("org", org);
    setSearchParams(params);
  }, [setSearchParams]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    updateUrlParams(value, filterOrganization);
  }, [updateUrlParams, filterOrganization]);

  const handleOrganizationFilterChange = useCallback((value: string) => {
    setFilterOrganization(value);
    updateUrlParams(searchTerm, value);
  }, [updateUrlParams, searchTerm]);

  const handleViewRoleplay = useCallback(
    (id: string, orgId: string) => {
      navigate(`/dashboard/roleplays/${orgId}/${id}`);
    },
    [navigate]
  );

  const filteredRoleplays = useMemo(() => {
    let data = roleplays;

    if (organizationId) {
      data = data.filter(
        (roleplay) => roleplay.organization_id === organizationId
      );
    }

    // Filter to show only active roleplays (latest version per resource_key)
    const latestActiveByOrgAndKey = new Map();
    data.forEach((roleplay) => {
      if (roleplay.is_active) {
        const resourceKey = roleplay.resource_key || roleplay.id;
        const orgId = roleplay.organization_id || "no-org";
        const compositeKey = `${orgId}::${resourceKey}`;

        const existing = latestActiveByOrgAndKey.get(compositeKey);

        if (
          !existing ||
          new Date(roleplay.creation_time) > new Date(existing.creation_time)
        ) {
          latestActiveByOrgAndKey.set(compositeKey, roleplay);
        }
      }
    });

    data = Array.from(latestActiveByOrgAndKey.values());

    // Apply search filter
    if (searchTerm) {
      data = data.filter((roleplay) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (roleplay.display_title &&
            roleplay.display_title.toLowerCase().includes(searchLower)) ||
          roleplay.description.toLowerCase().includes(searchLower) ||
          roleplay.human_role.toLowerCase().includes(searchLower) ||
          roleplay.bot_role.toLowerCase().includes(searchLower) ||
          roleplay.id.toLowerCase().includes(searchLower) ||
          (roleplay.resource_key &&
            roleplay.resource_key.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply organization filter (from URL param, not prop)
    if (filterOrganization) {
      data = data.filter(
        (roleplay) => roleplay.organization_id === filterOrganization
      );
    }

    // Apply environment filter
    if (filterEnvironment) {
      data = data.filter(
        (roleplay) => roleplay.environment === filterEnvironment
      );
    }

    return data;
  }, [roleplays, searchTerm, filterOrganization, filterEnvironment, organizationId]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "status",
        headerName: "Status",
        width: 100,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            {params.row.is_active ? (
              <Chip label="Active" size="small" color="success" />
            ) : (
              <Chip label="Inactive" size="small" color="default" />
            )}
          </Box>
        ),
      },
      {
        field: "id",
        headerName: "Roleplay ID",
        flex: 1,
        minWidth: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
              }}
              title={params.value as string}
            >
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "display_title",
        headerName: "Title",
        flex: 1,
        minWidth: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
                fontWeight: params.value ? 500 : 400,
              }}
              title={(params.value as string) || "-"}
            >
              {params.value || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.5,
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
                px: 1,
              }}
              title={(params.value as string) || ""}
            >
              {params.value || ""}
            </Typography>
          </Box>
        ),
      },
      {
        field: "resource_key",
        headerName: "Resource Key",
        flex: 1,
        minWidth: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
                fontFamily: "monospace",
                fontSize: "0.8rem",
              }}
              title={(params.value as string) || ""}
            >
              {params.value || "-"}
            </Typography>
          </Box>
        ),
      },
      ...(organizationId
        ? []
        : [
            {
              field: "organization_id",
              headerName: "Organization",
              flex: 1,
              minWidth: 150,
              headerAlign: "center",
              align: "center",
              renderCell: (params: GridRenderCellParams) => {
                if (!params.value) return "None";
                const org = organizations.find((o) => o.id === params.value);
                const displayValue = org ? org.name : (params.value as string);
                return (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "90%",
                        textAlign: "center",
                      }}
                      title={displayValue}
                    >
                      {displayValue}
                    </Typography>
                  </Box>
                );
              },
            } as GridColDef,
          ]),
      {
        field: "environment",
        headerName: "Env",
        width: 70,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.875rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
              }}
              title={params.value as string}
            >
              {params.value as string}
            </Typography>
          </Box>
        ),
      },
      {
        field: "creation_time",
        headerName: "Created",
        width: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              {params.value
                ? format(new Date(params.value as string), "MM/dd HH:mm")
                : ""}
            </Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        disableColumnMenu: true,
        flex: 0.5,
        minWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() =>
                handleViewRoleplay(params.row.id, params.row.organization_id)
              }
            >
              View
            </Button>
          </Box>
        ),
        headerAlign: "center",
        align: "center",
      },
    ],
    [handleViewRoleplay, organizations, organizationId]
  );

  if (loading && roleplays.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Roleplays</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
          width: "100%",
        }}
      >
        <TextField
          label="Search Roleplays"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ flex: 1, minWidth: 300 }}
        />

        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            value={filterOrganization}
            onChange={(e) => handleOrganizationFilterChange(e.target.value)}
            label="Organization"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" sx={{ minWidth: 150 }}>
          <InputLabel>Environment</InputLabel>
          <Select
            value={filterEnvironment}
            onChange={(e) => setFilterEnvironment(e.target.value)}
            label="Environment"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="staging">Staging</MenuItem>
            <MenuItem value="production">Production</MenuItem>
            <MenuItem value="demo">Demo</MenuItem>
            <MenuItem value="qa">QA</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={() => {
            setSearchTerm("");
            setFilterOrganization("");
            setFilterEnvironment("");
            setSearchParams({});
          }}
        >
          Reset Filters
        </Button>
      </Box>

      <Box
        sx={{
          height: organizationId ? "40vh" : "calc(100vh - 200px)",
          width: "100%",
        }}
      >
        <DataGrid
          rows={filteredRoleplays}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "creation_time", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            "& .MuiDataGrid-row": {
              minHeight: "52px !important",
            },
            "& .MuiDataGrid-cell": {
              py: 1,
            },
          }}
        />
      </Box>
    </>
  );
};

export default RoleplaysTab;
